import type { BoardWindow, DurationKind, Priority } from "./types";

const DAY_MS = 86_400_000;

/** "2d 4h left" / "3h 12m overdue" — the phrasing used across the header and cards. */
export function humanizeMs(ms: number): string {
  const abs = Math.abs(ms);
  const days = Math.floor(abs / DAY_MS);
  const hours = Math.floor((abs % DAY_MS) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${Math.max(1, minutes)}m`;
}

/** A board's end in ms; an open board (no deadline) never ends, so it never expires. */
export function boardEndMs(endsAt: string | null): number {
  return endsAt === null ? Number.POSITIVE_INFINITY : new Date(endsAt).getTime();
}

/** Latest end first, open boards ahead of any dated one. */
export function byEndDescending(a: { board: { endsAt: string | null } }, b: { board: { endsAt: string | null } }): number {
  const x = boardEndMs(a.board.endsAt);
  const y = boardEndMs(b.board.endsAt);
  return x === y ? 0 : y > x ? 1 : -1;
}

export function remainingLabel(window: BoardWindow, now = Date.now()): string {
  if (window.endsAt === null) return "no deadline";
  const remaining = new Date(window.endsAt).getTime() - now;
  return remaining <= 0 ? `${humanizeMs(remaining)} over` : `${humanizeMs(remaining)} left`;
}

/** Colour ramp for the deadline bar: calm early, amber near the end, red once past. */
export function urgencyColor(window: BoardWindow, now = Date.now()): string {
  if (window.endsAt === null) return "var(--kind-active)";
  const total = Math.max(1, new Date(window.endsAt).getTime() - new Date(window.startsAt).getTime());
  const elapsed = now - new Date(window.startsAt).getTime();
  const ratio = elapsed / total;
  if (ratio >= 1) return "var(--kind-blocked)";
  if (ratio >= 0.85) return "var(--prio-urgent)";
  if (ratio >= 0.6) return "var(--kind-review)";
  return "var(--kind-active)";
}

export function progressPercent(window: BoardWindow, now = Date.now()): number {
  if (window.endsAt === null) return 0;
  const total = Math.max(1, new Date(window.endsAt).getTime() - new Date(window.startsAt).getTime());
  const elapsed = now - new Date(window.startsAt).getTime();
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDue(iso: string | null, now = Date.now()): string {
  if (!iso) return "no date";
  const date = new Date(iso);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const dayDelta = Math.round((date.getTime() - startOfToday.getTime()) / DAY_MS);
  if (dayDelta === 0) return "today";
  if (dayDelta === 1) return "tomorrow";
  if (dayDelta === -1) return "yesterday";
  if (dayDelta > 1 && dayDelta < 7) return WEEKDAYS[date.getDay()]!;
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}, ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function relativeTime(iso: string, now = Date.now()): string {
  const delta = now - new Date(iso).getTime();
  if (delta < 60_000) return "just now";
  return `${humanizeMs(delta)} ago`;
}

/** "waiting 3h" — how long something has been waiting on the user, from `since`. */
export function waitingLabel(since: string, now = Date.now()): string {
  const delta = now - new Date(since).getTime();
  if (!(delta >= 60_000)) return "just now";
  return `waiting ${humanizeMs(delta)}`;
}

/** `<input type="datetime-local">` needs local wall-clock text, not an ISO instant. */
export function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalInputValue(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export const PRIORITY_RANK: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

/**
 * Where a board's window would end, worked out the way the server does, so the
 * deadline is visible before saving. `customDate` is a `YYYY-MM-DD` input value
 * and only matters for a custom window. Null until a custom date is picked.
 */
export function previewBoardEnd(kind: DurationKind, customDate: string): Date | null {
  const now = new Date();
  const end = (() => {
    switch (kind) {
      case "day":
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59);
      case "week": {
        const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7) + 6);
        monday.setHours(23, 59);
        return monday;
      }
      case "month":
        return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59);
      case "quarter":
        return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0, 23, 59);
      case "year":
        return new Date(now.getFullYear(), 11, 31, 23, 59);
      case "custom":
        return customDate ? new Date(`${customDate}T23:59`) : null;
      case "open":
        return null;
    }
  })();
  return end && !Number.isNaN(end.getTime()) ? end : null;
}


/** Bytes in binary units, one decimal under ten: `1.4 GB`, `96 MB`. */
export function formatBytes(value: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let n = value;
  let unit = 0;
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024;
    unit++;
  }
  return `${n >= 10 || unit === 0 ? n.toFixed(0) : n.toFixed(1)} ${units[unit]}`;
}
